export type SupportedLocale = 'zh-CN' | 'zh-TW' | 'en-US';
export type MeasurementRegion = 'CN' | 'HK' | 'TW' | 'US';

export const DEFAULT_LOCALE: SupportedLocale = 'zh-CN';
export const DEFAULT_MEASUREMENT_REGION: MeasurementRegion = 'CN';

export const LOCALE_STORAGE_KEY = 'aeterna_locale';
export const MEASUREMENT_REGION_STORAGE_KEY = 'aeterna_measurement_region';

export const SUPPORTED_LOCALES: Array<{
  value: SupportedLocale;
  label: string;
  nativeLabel: string;
  hint: string;
}> = [
  {
    value: 'zh-CN',
    label: '简体中文',
    nativeLabel: '简体',
    hint: '中国大陆用户默认语言',
  },
  {
    value: 'zh-TW',
    label: '繁體中文',
    nativeLabel: '繁體',
    hint: '港澳台及繁体中文界面',
  },
  {
    value: 'en-US',
    label: 'English',
    nativeLabel: 'EN',
    hint: 'English interface and AI responses',
  },
];

export const MEASUREMENT_REGIONS: Array<{
  value: MeasurementRegion;
  label: string;
  hint: string;
}> = [
  { value: 'CN', label: '中国大陆', hint: '使用大陆常见单位与参考区间' },
  { value: 'HK', label: '香港', hint: '保留港澳常见报告习惯' },
  { value: 'TW', label: '台湾', hint: '保留台湾常见报告习惯' },
  { value: 'US', label: 'United States', hint: 'Prefer US units and ranges' },
];

export const translations = {
  'zh-CN': {
    'nav.main': '主导航',
    'nav.illuminate': '照见',
    'nav.ask': '解惑',
    'nav.knowledge': '真知',
    'nav.execute': '践行',
    'nav.profile': '个人中心',
    'settings.title': '设置',
    'settings.locale.section': '语言与地区',
    'settings.locale.title': '界面语言',
    'settings.locale.desc': '影响导航、设置、请求语言与 AI 回复语言。',
    'settings.region.title': '指标地区',
    'settings.region.desc': '用于后续选择单位、参考区间和报告解释口径。',
    'settings.locale.saved': '语言偏好已保存',
    'settings.locale.saving': '正在保存',
    'settings.locale.error': '语言偏好保存失败，请稍后重试',
    'settings.memory.section': 'AI 对我的理解',
    'settings.memory.title': '个性化学习',
    'settings.memory.on': '反馈会帮助后续回答更贴合你。',
    'settings.memory.off': '已暂停根据反馈学习。',
    'settings.memory.guard': '只展示可由你管理的表达偏好。',
    'settings.memory.refresh': '刷新 AI 画像',
    'settings.memory.refresh.title': '刷新',
    'settings.memory.loadError': '暂时无法读取 AI 画像',
    'settings.memory.saveError': '设置保存失败，请稍后重试',
    'settings.memory.deleteError': '删除失败，请稍后重试',
    'settings.memory.loading': '读取中',
    'settings.memory.empty': '还没有可管理的 AI 画像',
    'settings.memory.pause': '暂停 AI 个性化学习',
    'settings.memory.enable': '开启 AI 个性化学习',
    'settings.memory.reset': '重置',
    'settings.memory.confirm': '确认',
    'settings.about.section': '关于',
    'routeError.import.title': '页面资源已更新',
    'routeError.import.desc': '本地开发服务刚重启过，重新加载后就能继续。',
    'routeError.generic.title': '页面暂时打不开',
    'routeError.generic.desc': '可以先返回首页，或重新加载当前页面。',
    'routeError.reload': '重新加载',
    'routeError.home': '回到首页',
    'routeError.unknown': '页面暂时无法打开',
  },
  'zh-TW': {
    'nav.main': '主導航',
    'nav.illuminate': '照見',
    'nav.ask': '解惑',
    'nav.knowledge': '真知',
    'nav.execute': '踐行',
    'nav.profile': '個人中心',
    'settings.title': '設定',
    'settings.locale.section': '語言與地區',
    'settings.locale.title': '介面語言',
    'settings.locale.desc': '影響導航、設定、請求語言與 AI 回覆語言。',
    'settings.region.title': '指標地區',
    'settings.region.desc': '用於後續選擇單位、參考區間和報告解釋口徑。',
    'settings.locale.saved': '語言偏好已儲存',
    'settings.locale.saving': '正在儲存',
    'settings.locale.error': '語言偏好儲存失敗，請稍後重試',
    'settings.memory.section': 'AI 對我的理解',
    'settings.memory.title': '個人化學習',
    'settings.memory.on': '回饋會幫助後續回答更貼合你。',
    'settings.memory.off': '已暫停根據回饋學習。',
    'settings.memory.guard': '只展示可由你管理的表達偏好。',
    'settings.memory.refresh': '重新整理 AI 畫像',
    'settings.memory.refresh.title': '重新整理',
    'settings.memory.loadError': '暫時無法讀取 AI 畫像',
    'settings.memory.saveError': '設定儲存失敗，請稍後重試',
    'settings.memory.deleteError': '刪除失敗，請稍後重試',
    'settings.memory.loading': '讀取中',
    'settings.memory.empty': '還沒有可管理的 AI 畫像',
    'settings.memory.pause': '暫停 AI 個人化學習',
    'settings.memory.enable': '開啟 AI 個人化學習',
    'settings.memory.reset': '重置',
    'settings.memory.confirm': '確認',
    'settings.about.section': '關於',
    'routeError.import.title': '頁面資源已更新',
    'routeError.import.desc': '本地開發服務剛重新啟動，重新載入後就能繼續。',
    'routeError.generic.title': '頁面暫時打不開',
    'routeError.generic.desc': '可以先返回首頁，或重新載入目前頁面。',
    'routeError.reload': '重新載入',
    'routeError.home': '回到首頁',
    'routeError.unknown': '頁面暫時無法開啟',
  },
  'en-US': {
    'nav.main': 'Primary navigation',
    'nav.illuminate': 'Illuminate',
    'nav.ask': 'Ask',
    'nav.knowledge': 'Knowledge',
    'nav.execute': 'Act',
    'nav.profile': 'Profile',
    'settings.title': 'Settings',
    'settings.locale.section': 'Language & Region',
    'settings.locale.title': 'Interface Language',
    'settings.locale.desc': 'Applies to navigation, settings, request language, and AI replies.',
    'settings.region.title': 'Measurement Region',
    'settings.region.desc': 'Used later for units, reference ranges, and report interpretation.',
    'settings.locale.saved': 'Language preference saved',
    'settings.locale.saving': 'Saving',
    'settings.locale.error': 'Could not save language preference. Try again later.',
    'settings.memory.section': 'How AI Understands Me',
    'settings.memory.title': 'Personalized Learning',
    'settings.memory.on': 'Feedback helps future answers fit you better.',
    'settings.memory.off': 'Learning from feedback is paused.',
    'settings.memory.guard': 'Only preferences you can manage are shown here.',
    'settings.memory.refresh': 'Refresh AI profile',
    'settings.memory.refresh.title': 'Refresh',
    'settings.memory.loadError': 'Could not load AI profile right now',
    'settings.memory.saveError': 'Could not save settings. Try again later.',
    'settings.memory.deleteError': 'Delete failed. Try again later.',
    'settings.memory.loading': 'Loading',
    'settings.memory.empty': 'No manageable AI profile items yet',
    'settings.memory.pause': 'Pause AI personalized learning',
    'settings.memory.enable': 'Enable AI personalized learning',
    'settings.memory.reset': 'Reset',
    'settings.memory.confirm': 'Confirm',
    'settings.about.section': 'About',
    'routeError.import.title': 'Page resources were updated',
    'routeError.import.desc': 'The local dev server was restarted. Reload to continue.',
    'routeError.generic.title': 'This page cannot open right now',
    'routeError.generic.desc': 'Return home or reload the current page.',
    'routeError.reload': 'Reload',
    'routeError.home': 'Home',
    'routeError.unknown': 'This page cannot open right now',
  },
} as const;

export type TranslationKey = keyof (typeof translations)[typeof DEFAULT_LOCALE];

export function normalizeLocale(value: unknown): SupportedLocale {
  const raw = String(value ?? '').trim().replace(/_/g, '-').toLowerCase();
  if (raw === 'zh' || raw === 'zh-cn' || raw === 'zh-hans' || raw === 'cn') return 'zh-CN';
  if (raw === 'zh-tw' || raw === 'zh-hk' || raw === 'zh-hant' || raw === 'hk' || raw === 'tw') return 'zh-TW';
  if (raw === 'en' || raw === 'en-us' || raw.startsWith('en-')) return 'en-US';
  return DEFAULT_LOCALE;
}

export function normalizeMeasurementRegion(value: unknown): MeasurementRegion {
  const raw = String(value ?? '').trim().toUpperCase();
  if (raw === 'HK') return 'HK';
  if (raw === 'TW') return 'TW';
  if (raw === 'US') return 'US';
  return DEFAULT_MEASUREMENT_REGION;
}

export function getStoredLocale(): SupportedLocale {
  try {
    return normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function getStoredMeasurementRegion(): MeasurementRegion {
  try {
    return normalizeMeasurementRegion(window.localStorage.getItem(MEASUREMENT_REGION_STORAGE_KEY));
  } catch {
    return DEFAULT_MEASUREMENT_REGION;
  }
}

export function setStoredLocale(locale: SupportedLocale) {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* storage may be unavailable in private mode */
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

export function setStoredMeasurementRegion(region: MeasurementRegion) {
  try {
    window.localStorage.setItem(MEASUREMENT_REGION_STORAGE_KEY, region);
  } catch {
    /* storage may be unavailable in private mode */
  }
}

export function t(locale: SupportedLocale, key: TranslationKey): string {
  return translations[locale][key] ?? translations[DEFAULT_LOCALE][key] ?? key;
}
